import React, { CSSProperties, useMemo } from 'react';
import classes from './CheckboxList.module.scss';
import { MandatoryChildren } from '../../../models/children-props';
import { CheckboxViewProps } from '../Checkbox/Checkbox.view';
import { SelectAllValidationType } from '../../actions/SelectAllCheckboxes/SelectAllCheckboxes.view';
import { UseFormReturn } from 'react-hook-form';
import classNames from 'classnames';
import { idGenerator } from '../../../helpers/idGenerator';
import inputClasses from '../form.module.scss';
import { ErrorRender } from '../../containers/ErrorRender';
import { SelectAllCheckboxes } from '../../actions/SelectAllCheckboxes';
import { CheckboxControllerProps } from '../Checkbox/Checkbox.controller';

export type SelectAllType =
  | {
      checkboxesChildrenGroupName: string;
      selectAll: true;
      selectAllParentName?: string;
      selectAllLabel?: string;
      selectAllName?: string;
      selectAllSize: CheckboxViewProps['size'];
      selectAllValidation?: SelectAllValidationType;
    }
  | {
      checkboxesChildrenGroupName: never;
      selectAll: false;
      selectAllParentName?: never;
      selectAllLabel?: never;
      selectAllName?: never;
      selectAllSize: never;
      selectAllValidation?: never;
    };

export type CheckboxListViewProps = MandatoryChildren &
  SelectAllType & {
    //
    borderless?: boolean;
    disabled?: boolean;
    formInstance: UseFormReturn;
    id?: string;
    label: string;
    wrapper?: CheckboxViewProps['wrapper'];
    maxHeight?: number;
    wrapperClickable?: boolean;
    stopPropagation?: boolean;
  };

export const CheckboxListView = (props: CheckboxListViewProps): JSX.Element => {
  let buffer: JSX.Element;

  const {
    formState: { errors }
  } = props.formInstance;

  const inputID = useMemo(() => props.id ?? idGenerator(), [props.id]);
  const errorClasses = classNames(inputClasses.HelpTextErrorClass, inputClasses.HelpText);

  const selectAllName = props.selectAllName || `select_all_checkboxes`;
  let numberOfChildren = 0;

  // Make sure we disable all the children if we pass disabled
  const childrenWithProps = React.Children.map(props.children, child => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child)) {
      numberOfChildren++;
    }
    if (React.isValidElement<CheckboxControllerProps>(child) && props.disabled) {
      const newProps = {
        ...child.props,
        disabled: true
      };
      return React.cloneElement(child, newProps);
    }
    return child;
  });

  const globalClasses = classNames(props.borderless ? classes.CheckboxListBorderless : classes.CheckboxListDefault, {
    [classes.CheckboxListError]: errors.hasOwnProperty(selectAllName)
  });
  const childrenWrapper = classNames({
    [classes.Scrollable]: !!props.maxHeight
  });
  const labelClasses = classNames({
    [inputClasses.Label]: true,
    [inputClasses.LabelDisabled]: props.disabled
  });
  const separatorClasses = classNames(classes.Hr, {
    [classes.HrError]: errors.hasOwnProperty(selectAllName)
  });

  const label = useMemo(
    () =>
      props.label ? (
        <label className={labelClasses} htmlFor={selectAllName}>
          {props.label}
        </label>
      ) : null,
    [props.label, labelClasses, selectAllName]
  );

  const selectAllCheckboxes =
    props.selectAll && numberOfChildren > 1 ? (
      <>
        <SelectAllCheckboxes
          id={inputID}
          formFieldName={props.checkboxesChildrenGroupName}
          formInstance={props.formInstance}
          label={props.selectAllLabel || 'Select All'}
          size={props.selectAllSize}
          name={selectAllName}
          wrapperClickable={props.wrapperClickable}
          {...(props.stopPropagation ? { stopPropagation: true } : {})}
          {...(props.selectAllValidation && !props.disabled ? { rules: props.selectAllValidation } : {})}
          {...(props.selectAllParentName ? { formFieldNamePrincipal: props.selectAllParentName } : {})}
          {...(props.disabled ? { disabled: true } : {})}
        />
        {!props.borderless && <hr className={separatorClasses} />}
      </>
    ) : null;

  const childrenStyle: CSSProperties = props.maxHeight
    ? {
        maxHeight: `${props.maxHeight}px`
      }
    : {};

  buffer = (
    <>
      {label}
      <div className={globalClasses}>
        {props.borderless && !!selectAllCheckboxes ? (
          <div
            className={classNames(classes.BorderlessWrapper, {
              [classes.BorderlessWrapperIsOnDropdown]: props.wrapperClickable
            })}>
            {selectAllCheckboxes}
          </div>
        ) : (
          selectAllCheckboxes
        )}
        <div className={childrenWrapper} style={childrenStyle}>
          {childrenWithProps}
        </div>
      </div>
      {errors && <ErrorRender errors={errors} name={selectAllName} className={errorClasses} />}
    </>
  );

  if (props.wrapper) {
    buffer = <div className={'input-wrapper'}>{buffer}</div>;
  }

  return buffer;
};
